import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Users2, Clock, Edit, Trash2 } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Input } from '../Components/ui/input';
import { Badge } from '../Components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../Components/ui/dialog';
import { Label } from '../Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';
import { Textarea } from '../Components/ui/textarea';
import { Avatar, AvatarFallback } from '../Components/ui/avatar';

export default function TalentBench() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const queryClient = useQueryClient();

  const { data: candidates = [] } = useQuery({
    queryKey: ['benchCandidates'],
    queryFn: () => backendApi.entities.BenchCandidate.list('-created_date', 100),
    initialData: []
  });

  const createMutation = useMutation({
    mutationFn: (data) => backendApi.entities.BenchCandidate.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['benchCandidates']);
      setIsDialogOpen(false);
      setEditingCandidate(null);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.BenchCandidate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['benchCandidates']);
      setIsDialogOpen(false);
      setEditingCandidate(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.BenchCandidate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['benchCandidates']);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    if (editingCandidate) {
      updateMutation.mutate({ id: editingCandidate.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || candidate.bench_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusColors = {
    'Available': 'bg-green-100 text-green-800 border-green-300',
    'In Process': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Assigned': 'bg-blue-100 text-blue-800 border-blue-300',
    'Inactive': 'bg-gray-100 text-gray-800 border-gray-300'
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-3 rounded-lg">
              <Users2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Talent Bench</h1>
              <p className="text-sm text-gray-500">Manage your talent pool</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingCandidate(null)}
                className="bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 shadow-md"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Bench
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingCandidate ? 'Edit Candidate' : 'Add to Talent Bench'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input name="full_name" defaultValue={editingCandidate?.full_name} required />
                  </div>
                  <div>
                    <Label>Applicant ID</Label>
                    <Input name="applicant_id" defaultValue={editingCandidate?.applicant_id} />
                  </div>
                  <div>
                    <Label>Experience (Years)</Label>
                    <Input type="number" name="experience_years" defaultValue={editingCandidate?.experience_years} />
                  </div>
                  <div>
                    <Label>Availability</Label>
                    <Select name="availability" defaultValue={editingCandidate?.availability || 'Immediate'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Immediate">Immediate</SelectItem>
                        <SelectItem value="2 Weeks">2 Weeks</SelectItem>
                        <SelectItem value="1 Month">1 Month</SelectItem>
                        <SelectItem value="2+ Months">2+ Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="bench_status" defaultValue={editingCandidate?.bench_status || 'Available'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Available">Available</SelectItem>
                        <SelectItem value="In Process">In Process</SelectItem>
                        <SelectItem value="Assigned">Assigned</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Last Contact Date</Label>
                    <Input type="date" name="last_contact_date" defaultValue={editingCandidate?.last_contact_date} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea name="notes" defaultValue={editingCandidate?.notes} rows={3} />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingCandidate ? 'Update' : 'Add'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
          <Input
            placeholder="Search candidates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-white shadow-sm border-gray-200"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-12 bg-white shadow-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Available">Available</SelectItem>
            <SelectItem value="In Process">In Process</SelectItem>
            <SelectItem value="Assigned">Assigned</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCandidates.map((candidate) => (
          <Card key={candidate.id} className="hover:shadow-lg transition-all duration-300 border border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-start space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white font-semibold">
                    {candidate.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'TC'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">{candidate.full_name}</CardTitle>
                  <Badge className={`${statusColors[candidate.bench_status]} border mt-1`}>
                    {candidate.bench_status}
                  </Badge>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingCandidate(candidate);
                      setIsDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (window.confirm('Remove from bench?')) {
                        deleteMutation.mutate(candidate.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {candidate.experience_years && (
                <div className="text-sm text-gray-600">
                  Experience: {candidate.experience_years} years
                </div>
              )}
              {candidate.availability && (
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  Available: {candidate.availability}
                </div>
              )}
              {candidate.last_contact_date && (
                <div className="text-xs text-gray-500">
                  Last contact: {new Date(candidate.last_contact_date).toLocaleDateString()}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCandidates.length === 0 && (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Users2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates on bench</h3>
            <p className="text-gray-500">Add candidates to your talent pool</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}